import React, { useContext, useEffect } from 'react';
import { EventDetailsContext } from '../../context/event_details.context';
import { EmailShareButton } from 'react-share';
import { createQueryString } from '../../../utilities/utilities';
import { trackActivity } from '../../../services/activity/activity.api.service';
import { ADDEVENT_EVENT_BASE_URL } from '../../constants/event_details.constants';
import './addevent_global_styles.scss';

const TARGET_TYPE = 'Event';
const ACTIVITY = 'share';

declare const window: Window & {
  addeventatc: {
    refresh: () => void;
  };
  location: {
    origin: string;
    pathname: string;
    search: string;
  };
  plausible: (eventName: string, props?: Record<string, unknown>) => void;
};

const sharedUrl =
  window.location.origin +
  window.location.pathname +
  createQueryString({
    utm_source: 'devex',
    utm_medium: 'email',
    utm_campaign: 'event_registration',
  });

const ShareSection: React.FC = () => {
  const { event } = useContext(EventDetailsContext);
  if (!event) return null;

  useEffect(() => {
    // Display an Addevent add-to-calendar button in this component rendered inside 
    // <ConfirmationModal />.

    // On mount, we should load the Addevent script that is responsible for rendering the button.
    // Given the nature of the AddEvent global script, we need to refresh it right after we
    // display the add-to-calendar button.
    // See more here https://www.addevent.com/c/documentation/add-to-calendar-button#anchor-faq
    // The issue of the script not being correctly applied does not occur on Localhost but did
    // show up on Develop, Master and Prod ENVs.
    // If the script is already loaded, refreshing it will not harm the experience at all.
    const script = document.createElement('script');
    script.id = 'addevent_script';
    script.src = 'https://cdn.addevent.com/libs/atc/1.6.1/atc.min.js';
    script.defer = true;
    script.onload = function() {
      setTimeout(() => {
        window.addeventatc?.refresh();
      }, 400);
    };
    document.body.append(script);
  }, []);

  const renderAddeventButton: JSX.Element = (
    <a
      // Undo all styles applied by AddEvent script. Prefer to manually overwrite those styles.
      data-styling='none'
      data-dropdown-y='up'
      data-dropdown-x='left'
      title='Add to Calendar'
      data-testid='addevent-add-to-calendar-button'
      className='addeventatc'
      data-id={event.addevent_id}
      href={ADDEVENT_EVENT_BASE_URL + event.addevent_id}
      target='_blank'
      rel='noopener noreferrer'
    >
      <span className='btn btn-outline-primary text-orange-devex'>
        Add to calendar
      </span>
    </a>
  );

  return (
    <div data-testid='share-event-section' className='d-flex justify-content-evenly'>
      { event.addevent_id && renderAddeventButton}
      <EmailShareButton
        htmlTitle='Share via email'
        url={sharedUrl}
        openShareDialogOnClick={true}
        // Disable onClick handler to open email window in a new tab
        // See https://github.com/nygardk/react-share/issues/153
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        onClick={() => {
          trackActivity({
            activity: ACTIVITY,
            target_id: event.id.toString(),
            target_type: TARGET_TYPE,
            detail: 'email',
          });
        }}
        subject={`Devex event: ${event.title}`}
        body={`I thought you might be interested in joining this event:\n\n${event.title}\n\n`}
      >
        <span className='btn btn-outline-primary'>Invite your colleagues</span>
      </EmailShareButton>
    </div>
  );
};

export default ShareSection;
